import type { IframeCodeErrorResponse } from '@src/ui/Editor/components/modals/EditorIframeCodeModal';
import { ERROR_MESSAGES } from '@src/utils/errorMessages';
import type { Editor } from '@tiptap/react';
import { useCallback, useState } from 'react';

export interface IFrameModalReturn {
  isIFrameCodeModalOpen: boolean;
  iFrameCode: string;
  iFrameCodeErrors: IframeCodeErrorResponse;

  openIFrameCodeModal: () => void;
  closeIFrameCodeModal: () => void;
  setIFrameCode: (code: string) => void;
  setIFrameCodeErrors: (errors: IframeCodeErrorResponse) => void;

  handleSubmitIFrameCode: (code: string) => void;
  insertIframe: (code: string) => void;
}

/**
 * useIFrameModal
 * Unified hook for iframe modal state management + business logic
 */
export function useIFrameModal(editor: Editor | null): IFrameModalReturn {
  const [isIFrameCodeModalOpen, setIsIFrameCodeModalOpen] = useState(false);
  const [iFrameCode, setIFrameCode] = useState('');
  const [iFrameCodeErrors, setIFrameCodeErrors] = useState<IframeCodeErrorResponse>({});

  const openIFrameCodeModal = useCallback(() => {
    setIFrameCode('');
    setIFrameCodeErrors({});
    setIsIFrameCodeModalOpen(true);
  }, []);

  const closeIFrameCodeModal = useCallback(() => setIsIFrameCodeModalOpen(false), []);

  const insertContentWithParagraph = useCallback(
    (insertFn: () => void) => {
      if (!editor) {
        return;
      }

      editor.view.dom.focus({ preventScroll: true });
      insertFn();
      setTimeout(() => {
        const pos = editor.state.selection.$to.pos;
        editor.view.dom.focus({ preventScroll: true });
        editor.chain().insertContentAt(pos, { type: 'paragraph' }).run();
      }, 0);
    },
    [editor],
  );

  const insertIframe = useCallback(
    (code: string) => {
      insertContentWithParagraph(() => {
        editor?.chain().focus().insertContent(code).run();
      });
    },
    [editor, insertContentWithParagraph],
  );

  const handleSubmitIFrameCode = useCallback(
    (code: string) => {
      if (!editor) {
        return;
      }

      if (!code.trim()) {
        setIFrameCodeErrors({
          value: [ERROR_MESSAGES.IFRAME.REQUIRED],
        });
        return;
      }

      const iframeTagRegex = /<iframe.*?>.*?<\/iframe>/i;
      if (!iframeTagRegex.test(code)) {
        setIFrameCodeErrors({
          value: [ERROR_MESSAGES.IFRAME.INVALID_FORMAT],
        });
        return;
      }

      const iFrameValidService = ['youtube.com', 'youtu.be', 'vimeo.com', 'facebook.com'];
      if (!iFrameValidService.some((service) => code.includes(service))) {
        setIFrameCodeErrors({
          value: [ERROR_MESSAGES.IFRAME.INVALID_SERVICE],
        });
        return;
      }

      insertIframe(code);
      closeIFrameCodeModal();
      setIFrameCode('');
      setIFrameCodeErrors({});
    },
    [editor, insertIframe, closeIFrameCodeModal],
  );

  return {
    isIFrameCodeModalOpen,
    iFrameCode,
    iFrameCodeErrors,

    openIFrameCodeModal,
    closeIFrameCodeModal,
    setIFrameCode,
    setIFrameCodeErrors,

    handleSubmitIFrameCode,
    insertIframe,
  };
}
